import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo, Fragment, JSX } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SubAccountSearchForm, { SubAccountStatus } from './SubAccountSearchForm';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import Table, { createTableProps, Row } from './Table/Table';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import { Title, TableFiltersContainer } from '@/components/UI';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import useBlockAccounts from '@/hooks/useApi/useBlockAccounts';
import styles from './SubAccount.module.scss';
import OrgChartContainer from './OrgChartContainer/OrgChartContainer';
import { Button, UtilButton, Table as TableUI } from '@/components/UI';
import { useTranslation } from 'react-i18next';
import AccountPermissionsDrawer from './Permissions/AccountPermissionsDrawer';
import { useDialog } from '@/providers/Dialog.provider';
import { BlockDialog, UnBlockDialog } from './BlockingDialog';
import { BodyText, MaterialSymbol } from 'vopay';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

const { Box, formattedCurrency, useUpdateQueryParams, withStyles, Card } = vp;

const classes = withStyles(styles);

type QueryParams = {
  status: SubAccountStatus;
  currency: string;
  country: string;
  searchTerm: string;
};

type FC = FunctionComponent;
export type NestedSubAccount = vp.SubAccountsListsShow & {
  SubAccounts?: NestedSubAccount[];
  Key: string;
};
export type FlatSubAccountWithTree = vp.SubAccountsListsShow & {
  tree: string[]; // Array of parent IDs
};
const SubAccountsListContainer: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const status: SubAccountStatus = useMemo(
    () => (queryParams.get('status') as SubAccountStatus) || 'Active',
    [queryParams],
  );
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List' });

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { permissions } = usePermissions();
  const [subAccountsResponse, , , fetchSubAccounts] = useFetchSubAccounts({});
  const { blockAccounts, unblockAccounts } = useBlockAccounts();
  const [, setLoading] = useDataFetchIndicator();
  const { onSubNavClick } = useNavMenu();
  const { setNewCurrencyInfo } = useAccountInfo();
  const [nestedSubAccounts, setNestedSubAccounts] = useState<NestedSubAccount[]>([]);
  const [flatSubAccountWithTree, setFlatSubAccountWithTree] = useState<FlatSubAccountWithTree[]>([]);
  const [flatSubAccountFiltered, setFlatSubAccountFiltered] = useState<FlatSubAccountWithTree[]>([]);
  const [hasSearch, setHasSearch] = useState(false);
  const [showOrgView, setShowOrgView] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [selectedAccountName, setSelectedAccountName] = useState<string>();
  const dialogue = useDialog();
  const [showTransactionFilter, setShowTransactionFilter] = useState(true);
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();

  const {
    response: { clientAccountsEnabled, AccountID, AccountName, ShowSubAccountOrgChart, AccountType },
  } = useClientLogin();

  useEffect(() => {
    const fetchData = async () => {
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
      });
      if (!resp.Success) {
        toast.neutral(resp.ErrorMessage);
      }
    };
    fetchData();
  }, []);

  const matchesCurrency = (data: vp.SubAccountsListsShow, newCurrency: string) =>
    newCurrency === '' || newCurrency.toLocaleLowerCase() === 'all' || data.Currency === newCurrency;

  const matchesCountry = (data: vp.SubAccountsListsShow, newCountry: string) =>
    newCountry === '' || newCountry.toLocaleLowerCase() === 'all' || data.Country === newCountry;

  const matchesStatus = (data: vp.SubAccountsListsShow, newStatus: SubAccountStatus) => {
    const sanitizedStatus = data.IsActive ? 'Active' : 'NotActive';
    return (
      newStatus === '' ||
      newStatus.toLocaleLowerCase() === 'all' ||
      sanitizedStatus === newStatus ||
      (newStatus === 'NotActive' && sanitizedStatus !== 'Active')
    );
  };

  const matchesSearchTerm = (data: vp.SubAccountsListsShow, newSearchTerm: string) => {
    const keys: (keyof vp.SubAccountsListsShow)[] = ['AccountName'];
    return (
      newSearchTerm === '' ||
      keys.some((key) => data[key]?.toString().toLowerCase().includes(newSearchTerm.toLowerCase()))
    );
  };

  const search = ({
    searchTerm: newSearchTerm,
    status: newStatus,
    currency: newCurrency,
    country: newCountry,
    flatSubAccountWithTree: newFlatSubAccountWithTree,
  }: {
    searchTerm: string;
    status: SubAccountStatus;
    currency: string;
    country: string;
    flatSubAccountWithTree: FlatSubAccountWithTree[];
  }) => {
    const filteredData = newFlatSubAccountWithTree.filter(
      (data) =>
        matchesCurrency(data, newCurrency) &&
        matchesCountry(data, newCountry) &&
        matchesStatus(data, newStatus) &&
        matchesSearchTerm(data, newSearchTerm),
    );

    setFlatSubAccountFiltered(filteredData);

    const hasSearch =
      newSearchTerm !== '' ||
      (newStatus !== '' && newStatus.toLocaleLowerCase() !== 'all') ||
      (newCurrency !== '' && newCurrency.toLocaleLowerCase() !== 'all') ||
      (newCountry !== '' && newCountry.toLocaleLowerCase() !== 'all');

    updateQueryParams({ status: newStatus, currency: newCurrency, country: newCountry, searchTerm: newSearchTerm });

    setHasSearch(hasSearch);
  };

  useEffect(() => {
    const { nested, flatWithTree } = buildNestedAndFlatWithTree(
      AccountID,
      AccountName,
      mapBalances(Object.values(subAccountsResponse.Subaccounts || {})),
    );

    setNestedSubAccounts(nested);
    setFlatSubAccountWithTree(flatWithTree);
    const searchParams = {
      status,
      currency,
      country,
      searchTerm,
      flatSubAccountWithTree: flatWithTree,
    };
    updateQueryParams(searchParams);
    search(searchParams);

    // Extract unique countries for the dropdown
    const subAccountsArray = Object.values(subAccountsResponse.Subaccounts || {});
    const mappedSubAccounts = mapBalances(subAccountsArray);
    const uniqueCountries = [...new Set(mappedSubAccounts.map((item) => item.Country).filter(Boolean))].sort();
    setCountries(uniqueCountries);
  }, [subAccountsResponse]);

  // Determine if all accounts are blocked/locked
  const allAccountsBlocked = useMemo(() => {
    const subAccounts = Object.values(subAccountsResponse.Subaccounts || {});
    if (subAccounts.length === 0) return false;

    const allAccountBalances = mapBalances(subAccounts);
    return allAccountBalances.every((account) => account.AccountStatus === 'Blocked');
  }, [subAccountsResponse]);

  const tableProps = createTableProps({
    id: 'subAccountsList',

    hasPlaceholderArrows: true,
    rows: mapNestedSubAccounts(nestedSubAccounts),
    columns: [
      {
        id: 'accountName',
        header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
        align: 'left',

        template: ({ data }) => {
          return (
            <TableUI.Link
              to={{
                pathname: `/sub-accounts-list/details/${data.AccountID.replace(/\s+/g, '')}/${data.Currency}`,
                state: { data },
              }}
            >
              {data.AccountName}
            </TableUI.Link>
          );
        },
      },
      {
        id: 'status',
        header: t('Attributes.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={data.IsActive ? 'active' : 'not active'}
            label={
              data.IsActive
                ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
            }
          />
        ),
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        id: 'country',
        header: t('Attributes.country', { defaultValue: 'Country' }) as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pendingBalance',
        header: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2D0D' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.PendingBalance)}`}
          </span>
        ),
      },
      {
        id: 'availableBalance',
        header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2D0D' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.AvailableBalance)}`}
          </span>
        ),
        hasDivider: false,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {!clientAccountsEnabled && data.IsActive && (
              <Button
                id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
                size="smallest"
                colorScheme="secondary"
                onClick={() => {
                  onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                  setNewCurrencyInfo(data.Currency);
                  history.push({
                    pathname: '/sub-account-transfer',
                    state: {
                      token: data.Token,
                      currency: data.Currency,
                    },
                  });
                }}
              >
                {t('Table.Row.transferButton', { defaultValue: 'Transfer' })}
              </Button>
            )}
            <Button
              id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
              size="smallest"
              colorScheme="secondary"
              onClick={() => {
                setSelectedAccountId(data.AccountID);
                setSelectedAccountName(data.AccountName);
                setIsPermissionsDrawerOpen(true);
              }}
            >
              {t('Table.Row.permissionsButton', { defaultValue: 'Permissions' })}
            </Button>
          </Box>
        ),
      },
    ],
  });
  const searchTableProps = createTableProps({
    id: 'subAccountsList',
    hasPlaceholderArrows: false,

    rows: mapFlatSubAccountWithTree(flatSubAccountFiltered),
    columns: [
      {
        id: 'accountName',
        header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
        align: 'left',
        template: ({ data }) => {
          const onClick = (value: string) => {
            search({ status: '', currency, country: '', searchTerm: value, flatSubAccountWithTree });
          };
          return (
            <Box flx="col" ai="fs">
              {getBreadcrumbs(data.tree, onClick)}
              <TableUI.Link
                to={{
                  pathname: `/sub-accounts-list/details/${data.AccountID.replace(/\s+/g, '')}/${data.Currency}`,
                  state: { data },
                }}
              >
                {data.AccountName}
              </TableUI.Link>
            </Box>
          );
        },
      },
      {
        id: 'status',
        header: t('Attributes.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={data.IsActive ? 'active' : 'not active'}
            label={
              data.IsActive
                ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
            }
          />
        ),
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        id: 'country',
        header: t('Attributes.country', { defaultValue: 'Country' }) as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pendingBalance',
        header: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2DB3' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.PendingBalance)}`}
          </span>
        ),
      },
      {
        id: 'availableBalance',
        header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2DB3' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.AvailableBalance)}`}
          </span>
        ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {clientAccountsEnabled && data.IsActive && (
              <Button
                id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
                colorScheme="secondary"
                size="smallest"
                onClick={() => {
                  onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                  setNewCurrencyInfo(data.Currency);
                  history.push({
                    pathname: '/sub-account-transfer',
                    state: {
                      token: data.Token,
                      currency: data.Currency,
                    },
                  });
                }}
              >
                {t('Table.Row.transferButton', { defaultValue: 'Transfer' })}
              </Button>
            )}

            <Button
              id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
              colorScheme="secondary"
              size="smallest"
              onClick={() => {
                setSelectedAccountId(data.AccountID);
                setSelectedAccountName(data.AccountName);
                setIsPermissionsDrawerOpen(true);
              }}
            >
              {t('Table.Row.permissionsButton', { defaultValue: 'Permissions' })}
            </Button>
          </Box>
        ),
      },
    ],
  });
  const handleBlockAccounts = async () => {
    try {
      setLoading(true);
      await blockAccounts();
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
      });
      if (!resp.Success) {
        throw new Error(resp.ErrorMessage);
      }
      toast.neutral(t('blockSuccessToast', { defaultValue: 'All Sub-Accounts have been blocked' }));
    } catch (error) {
      if (error instanceof Error) {
        toast.neutral(error.message);
      }
      console.error('Failed to block accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockAccounts = async () => {
    try {
      setLoading(true);
      await unblockAccounts();
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
      });
      if (!resp.Success) {
        throw new Error(resp.ErrorMessage);
      }
      toast.neutral(t('unblockSuccessToast', { defaultValue: 'All Sub-Accounts have been unblocked' }));
    } catch (error) {
      if (error instanceof Error) {
        toast.neutral(error.message);
      }
      console.error('Failed to unblock accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const blockSubAccounts = async () => {
    const confirmed = await dialogue.open(BlockDialog);
    if (confirmed) {
      await handleBlockAccounts();
    }
  };

  const unblockSubAccounts = async () => {
    const confirmed = await dialogue.open(UnBlockDialog);
    if (confirmed) {
      await handleUnblockAccounts();
    }
  };

  return (
    <Box flx="row" jc="center" className={classes('subAccountsListContainer')}>
      <Box flx="col" gapSize="33px" boxWidth="100%">
        <Box jc="sb" width="100%">
          <Title>{t('title', { defaultValue: 'Sub-Account List' })}</Title>
          <Box gapSize="16px" ai="center">
            {showOrgView && ShowSubAccountOrgChart && (
              <UtilButton
                onClick={() => {
                  setShowOrgView(false);
                }}
                iconLeftProps={{ icon: 'table_rows', fillAmount: 1 }}
              >
                {t('switchViewButton', { defaultValue: 'Switch View' })}
              </UtilButton>
            )}
            {!showOrgView && ShowSubAccountOrgChart && (
              <UtilButton
                iconLeft="lan"
                onClick={() => {
                  setShowOrgView(true);
                }}
              >
                {t('switchViewButton', { defaultValue: 'Switch View' })}
              </UtilButton>
            )}
            {AccountType === 'Secured Account' && permissions.ManageBlockedAccounts && (
              <>
                {!allAccountsBlocked && (
                  <Button colorScheme="secondary" onClick={blockSubAccounts}>
                    {t('blockSubAccountsButton', { defaultValue: 'Block Sub-Accounts' })}
                  </Button>
                )}
                {allAccountsBlocked && (
                  <Button colorScheme="secondary" onClick={unblockSubAccounts}>
                    {t('unblockSubAccountsButton', { defaultValue: 'Unblock Sub-Accounts' })}
                  </Button>
                )}
              </>
            )}
            {permissions.AddSubaccounts &&
              (lockedFeatures.SubAccountPermissions ? (
                <Button
                  id="add-sub-account"
                  colorScheme="primary"
                  onClick={() => {
                    onSubNavClick(
                      'createSubAccount',
                      '/create-sub-account',
                      t('createModuleName', { defaultValue: 'Create Sub-Account' }),
                    );
                    history.push('/create-sub-account');
                  }}
                  iconLeft="add"
                >
                  {t('createSubAccountButton', { defaultValue: 'Sub-Account' })}
                </Button>
              ) : (
                <Button
                  id="add-sub-account"
                  onClick={() => updateIsLocked(true, t('createModuleName', { defaultValue: 'Create Sub-Account' }))}
                  iconLeftProps={{ icon: 'lock' }}
                  colorScheme="primary"
                  className={styles.lockedButton}
                >
                  {t('addSubAccountButton', { defaultValue: 'Add Account' })}
                </Button>
              ))}
          </Box>
        </Box>
        {AccountType === 'Secured Account' && allAccountsBlocked && (
          <div className={classes('infoBox')}>
            <div className={classes('infoBoxHeader')}>
              <MaterialSymbol color="#2F82FF" size="20px" icon="block" />
              <BodyText weight="em">{t('blockedInfoTitle', { defaultValue: 'Sub-Accounts are Blocked' })}</BodyText>
            </div>
            <BodyText className={classes('infoBoxDescription')}>
              {t('blockedInfoDescription', {
                defaultValue:
                  'Outbound transactions are disabled and Inbound transactions will deposit to the parent account.',
              })}
            </BodyText>
          </div>
        )}
        {!showOrgView && (
          <Card
            style={{
              padding: '24px 16px',
            }}
          >
            <Box flx="col" pb="md" base="8">
              <TableFiltersContainer
                open={showTransactionFilter}
                onOpenChange={setShowTransactionFilter}
                filtersApplied={hasSearch}
              >
                <Box flx="col" pb="sm" base="8" ai="fe">
                  <TableFiltersContainer.Trigger />
                </Box>

                <TableFiltersContainer.Content>
                  <SubAccountSearchForm
                    status={status}
                    currency={currency}
                    country={country}
                    countries={countries}
                    searchTerm={searchTerm}
                    search={(values) =>
                      search({
                        ...values,
                        flatSubAccountWithTree,
                      })
                    }
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>

            <Box className={classes('subAccountsTableContainer', 'scrollbar')}>
              {!hasSearch && <Table {...tableProps} />}
              {hasSearch && <Table {...searchTableProps} />}
            </Box>
          </Card>
        )}
        {showOrgView && (
          <OrgChartContainer
            subAccounts={nestedSubAccounts}
            originalSubAccounts={subAccountsResponse.Subaccounts}
            accountBlocked={allAccountsBlocked}
            refresh={async () => {
              const resp = await fetchSubAccounts({
                PortalUser: AccountID,
                ShowToken: true,
              });

              if (!resp.Success) {
                toast.neutral(resp.ErrorMessage);
              }
            }}
          />
        )}
      </Box>
      <AccountPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        accountId={selectedAccountId}
        accountName={selectedAccountName}
      />
    </Box>
  );

  function mapBalances(subAccounts: vp.SubAccountsLists[]): vp.SubAccountsListsShow[] {
    const subAccountsShow: vp.SubAccountsListsShow[] = [];
    subAccounts.forEach((subAccount) => {
      Object.values(subAccount.Balances || {}).forEach((balance) => {
        subAccountsShow.push({ ...subAccount, ...balance });
      });
    });

    return subAccountsShow;
  }
  function buildNestedAndFlatWithTree(
    parentId: string,
    parentName: string,
    subAccounts: vp.SubAccountsListsShow[],
  ): { nested: NestedSubAccount[]; flatWithTree: FlatSubAccountWithTree[] } {
    const flatWithTree: FlatSubAccountWithTree[] = [];

    function buildTree(currentParentId: string, currentParentName: string, currentTree: string[]): NestedSubAccount[] {
      const result: NestedSubAccount[] = [];

      for (const key in subAccounts) {
        const account = subAccounts[key];

        if (account.ParentAccountID === currentParentId) {
          const newTree = [...currentTree, currentParentName];
          const children = buildTree(account.AccountID, account.AccountName, newTree);

          result.push({ ...account, SubAccounts: children, Key: `${account.AccountID}-${account.Currency}` });
          flatWithTree.push({ ...account, tree: newTree.slice(1) });
        }
      }

      return result;
    }

    // Start building the nested structure and flat structure with trees
    const nestedResult = buildTree(parentId, parentName, []);
    return { nested: nestedResult, flatWithTree };
  }
  function mapNestedSubAccounts(nestedSubAccounts: NestedSubAccount[]): Row<NestedSubAccount>[] {
    return (
      nestedSubAccounts.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: (account.SubAccounts || []).length > 0 ? mapNestedSubAccounts(account.SubAccounts || []) : undefined,
      })) || []
    );
  }
  function mapFlatSubAccountWithTree(flatSubAccountWithTree: FlatSubAccountWithTree[]): Row<FlatSubAccountWithTree>[] {
    return (
      flatSubAccountWithTree.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
      })) || []
    );
  }
  function getBreadcrumbs(tree: string[], onClick: (value: string) => void): JSX.Element {
    if (tree.length === 0) return <></>;

    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded(!expanded);

    const renderBreadcrumbItem = (item: string, index: number) => (
      <Fragment key={item}>
        <button type="button" onClick={() => onClick(item)}>
          {item}
        </button>
        {index < tree.length - 1 && ' > '}
      </Fragment>
    );

    const renderCollapsedBreadcrumbs = () => (
      <>
        {renderBreadcrumbItem(tree[0], 0)}
        {'>'}
        <button type="button" onClick={toggleExpanded}>
          ...
        </button>
        {'>'}
        {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
      </>
    );

    const breadcrumbs = (
      <span className={styles.breadcrumbs}>
        {tree.length <= 2 || expanded ? tree.map(renderBreadcrumbItem) : renderCollapsedBreadcrumbs()}
      </span>
    );

    return <nav aria-label={t('breadcrumbsLabel', { defaultValue: 'Breadcrumbs' })}>{breadcrumbs}</nav>;
  }
};

export default SubAccountsListContainer;
